import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';

import { useTypedParams } from '@sorare/routing';

import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';

import { PlayerGameScoreDialog } from '@sorare/marketplace/src/shared/stats/PlayerGameScoreDialog';

import { Matches } from '@sorare/gameplay/src/components/seo/Matches';

import type {
  NbaPlayerPastFixturesQuery,
  NbaPlayerPastFixturesQueryVariables,
} from './__generated__/page.graphql';
import type { PageParams } from './__generated__/routeParams';

const NBA_PLAYER_FIXTURES_QUERY = gql`
  query NbaPlayerPastFixturesQuery($slug: String!) {
    anyPlayer(slug: $slug) {
      slug
      displayName
      anyGameStats(last: 30) {
        id
        anyGame {
          id
          ...Matches_anyGameInterface
        }
      }
      activeClub {
        slug
        upcomingAnyGames(first: 100) {
          id
          ...Matches_anyGameInterface
        }
      }
    }
  }
  ${Matches.fragments.anyGameInterface}
` as TypedDocumentNode<
  NbaPlayerPastFixturesQuery,
  NbaPlayerPastFixturesQueryVariables
>;

export const PlayerGames = () => {
  const { slug } = useTypedParams<PageParams>();
  const [playerGameScoreId, setPlayerGameScoreId] = useState<string>();

  const [tab, setTab] = useState<'past' | 'future'>('future');
  const { data, loading } = usePaginatedQuery(NBA_PLAYER_FIXTURES_QUERY, {
    variables: { slug },
    connection: 'GameConnection',
  });

  const pastPlayerGameStats =
    data?.anyPlayer?.anyGameStats.filter(Boolean) || [];
  const futureGames =
    data?.anyPlayer?.activeClub?.upcomingAnyGames.filter(Boolean) || [];

  if (tab === 'future' && futureGames.length <= 1 && !loading) {
    setTab('past');
  }

  return (
    <>
      <Matches
        displayName={data?.anyPlayer?.displayName ?? ''}
        loading={loading}
        pastGames={pastPlayerGameStats.map(({ anyGame }) => ({
          ...anyGame,
          onClick: () => setPlayerGameScoreId(idFromObject(anyGame.id)),
        }))}
        futureGames={futureGames.map(anyGame => anyGame)}
        infiniteScrollLoader={null}
        tab={tab}
        setTab={setTab}
      />
      {!!playerGameScoreId && (
        <PlayerGameScoreDialog
          playerGameScoreId={playerGameScoreId}
          onClose={() => setPlayerGameScoreId(undefined)}
          open={!!playerGameScoreId}
          showUpsell={false}
        />
      )}
    </>
  );
};

export default PlayerGames;
